// TarifsResidenceCalculator.jsx

import React, { useMemo, useState } from "react";
import {
  FaCalculator,
  FaCalendarAlt,
  FaCheckCircle,
  FaEuroSign,
  FaHome,
  FaInfoCircle,
  FaLayerGroup,
  FaMapMarkerAlt,
} from "react-icons/fa";

const RESIDENCE_OPTIONS = [
  {
    id: "lhay",
    label: "L'Haÿ-les-Roses",
    shortLabel: "L'Haÿssiens",
    helper: "Habitant L'Haÿ-les-Roses",
  },
  {
    id: "antony",
    label: "Antony",
    shortLabel: "Antoniens",
    helper: "Habitant Antony",
  },
  {
    id: "outside",
    label: "Extérieurs",
    shortLabel: "Extérieurs",
    helper: "Autre commune",
  },
];

const DOJO_TARIFFS = [
  // ---------------------------------------------------------------------------
  // GYMNASE DAVID DOUILLET
  // ---------------------------------------------------------------------------
  {
    id: "david-douillet",
    name: "Gymnase David Douillet",
    address: "Rue Gustave Charpentier 94240 L'Haÿ-les-Roses",
    groups: [
      {
        code: "L - BG 1",
        name: "Baby's et Galipettes",
        birth: "nés en 2023 - 2022",
        schedules: ["Mercredi 13h45-14h30"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "L - BG 2",
        name: "Baby's et Galipettes",
        birth: "nés en 2023 - 2022",
        schedules: ["Samedi 9h00-9h45"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "L - BJ",
        name: "Baby's judo",
        birth: "nés en 2022 - 2021",
        schedules: ["Mercredi 14h30-15h15"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "L - COQ",
        name: "Coquilles",
        birth: "nés en 2020 ou 2019",
        schedules: ["Lundi et vendredi 17h30-18h30"],
        prices: {
          lhay: 205,
          antony: 225,
          outside: 245,
        },
      },
      {
        code: "L - COP 1",
        name: "Coquilles et Poussins",
        birth: "nés entre 2017 et 2020",
        note: "Cours unique 1h/semaine",
        schedules: ["Mercredi 15h15-16h15"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "L - COP 2",
        name: "Coquilles et Poussins",
        birth: "nés entre 2017 et 2020",
        note: "Cours unique 1h/semaine",
        schedules: ["Samedi 9h45-10h30"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "L - POB",
        name: "Poussins et Benjamins",
        birth: "nés entre 2015 et 2018",
        schedules: ["Lundi et vendredi 18h30-19h30"],
        prices: {
          lhay: 205,
          antony: 225,
          outside: 245,
        },
      },
      {
        code: "MCJS",
        name: "Minimes, Cadets, Juniors, Seniors",
        birth: "à partir de 2014",
        note: "Deuxième cours à Antony le lundi de 19h30 à 21h00",
        schedules: ["Vendredi 19h30-21h00"],
        prices: {
          lhay: 230,
          antony: 250,
          outside: 270,
        },
      },

      // -----------------------------------------------------------------------
      // JJB - DAVID DOUILLET
      // -----------------------------------------------------------------------
      {
        code: "JJB - ADULTES - DD",
        name: "Jiu-Jitsu Brésilien - Adultes",
        birth: "Adultes",
        discipline: "JJB",
        residenceIndependent: true,

        schedules: ["Mercredi 18h30-20h00"],

        prices: {
          lhay: 350,
          antony: 350,
          outside: 350,
        },

        mainTariffLabel: "Formule JJB seul",

        complementaryTariff: {
          label: "Formule JJB + Judo",
          value: 450,
          prefix: "À partir de",
        },

        note: "La formule JJB permet également d'accéder aux autres créneaux JJB proposés par le club.",
      },
      {
        code: "JJB - ENFANTS - DD",
        name: "Jiu-Jitsu Brésilien - Enfants",
        birth: "7 à 12 ans",
        discipline: "JJB",
        residenceIndependent: true,

        schedules: ["Mercredi 17h30-18h30"],

        prices: {
          lhay: 250,
          antony: 250,
          outside: 250,
        },

        mainTariffLabel: "Formule JJB seul",

        complementaryTariff: {
          label: "Formule JJB + Judo",
          value: 360,
          prefix: "À partir de",
        },

        note: "Cours de Jiu-Jitsu Brésilien réservé aux enfants de 7 à 12 ans.",
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // GYMNASE ARNAUD BELTRAME
  // ---------------------------------------------------------------------------
  {
    id: "arnaud-beltrame",
    name: "Gymnase Arnaud Beltrame",
    address: "16, rue Pierre Kohlmann 92160 Antony",
    groups: [
      {
        code: "A - BG",
        name: "Baby's et Galipettes",
        birth: "nés en 2023 - 2022",
        schedules: ["Samedi 9h00-9h45"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "A - BJ 1",
        name: "Baby's judo",
        birth: "nés en 2022 - 2021",
        schedules: ["Samedi 9h45-10h30"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "A - BJ 2",
        name: "Baby's judo",
        birth: "nés en 2022 - 2021",
        schedules: ["Samedi 10h30-11h15"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "A - BC",
        name: "Baby's et Coquilles",
        birth: "nés en 2021 - 2019",
        schedules: ["Mercredi 13h45-14h45"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "A - COQ",
        name: "Coquilles",
        birth: "nés en 2020 ou 2019",
        schedules: ["Mercredi 13h45-14h45", "Samedi 11h15-12h15"],
        prices: {
          lhay: 205,
          antony: 225,
          outside: 245,
        },
      },
      {
        code: "A - POB",
        name: "Poussins et Benjamins",
        birth: "nés entre 2018 et 2015",
        schedules: ["Lundi 18h00-19h00", "Mercredi 14h45-15h45"],
        prices: {
          lhay: 205,
          antony: 225,
          outside: 245,
        },
      },
      {
        code: "A - CP",
        name: "Cours préparatoire aux UV",
        birth: "accessible dès ceinture marron",
        schedules: ["Lundi 19h00-19h30"],
        prices: {
          lhay: null,
          antony: null,
          outside: null,
        },
      },
      {
        code: "MCJS",
        name: "Minimes, Cadets, Juniors, Seniors",
        birth: "à partir de 2014",
        note: "Deuxième cours à Douillet le vendredi de 19h30 à 21h00",
        schedules: ["Lundi 19h30-21h00"],
        prices: {
          lhay: 230,
          antony: 250,
          outside: 270,
        },
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // GYMNASE VALLÉE AUX RENARDS
  // ---------------------------------------------------------------------------
  {
    id: "vallee-aux-renards",
    name: "Gymnase la Vallée aux Renards",
    address: "24, rue Jean Moulin 94260 Fresnes",
    groups: [
      {
        code: "V - BG",
        name: "Baby's et Galipettes",
        birth: "nés en 2023 - 2021",
        schedules: ["Samedi 11h00-11h45"],
        prices: {
          lhay: 175,
          antony: 195,
          outside: 215,
        },
      },
      {
        code: "V - COQ",
        name: "Coquilles",
        birth: "nés en 2020 ou 2019",
        schedules: ["Mardi et vendredi 17h00-18h00"],
        prices: {
          lhay: 205,
          antony: 225,
          outside: 245,
        },
      },
      {
        code: "V - PO",
        name: "Poussins",
        birth: "nés en 2018 ou 2017",
        schedules: ["Mardi et vendredi 18h00-19h00"],
        prices: {
          lhay: 205,
          antony: 225,
          outside: 245,
        },
      },
      {
        code: "V - BEN",
        name: "Benjamins",
        birth: "nés entre 2016 et 2015",
        schedules: ["Mardi et vendredi 19h00-20h30"],
        prices: {
          lhay: 230,
          antony: 250,
          outside: 270,
        },
      },
      {
        code: "V - M",
        name: "Minimes",
        birth: "nés entre 2014 et 2013",
        schedules: ["Mardi et vendredi 19h00-20h30"],
        prices: {
          lhay: 230,
          antony: 250,
          outside: 270,
        },
      },

      // -----------------------------------------------------------------------
      // JJB - VALLÉE AUX RENARDS
      // -----------------------------------------------------------------------
      {
        code: "JJB - ADULTES - VR",
        name: "Jiu-Jitsu Brésilien - Adultes",
        birth: "Adultes",
        discipline: "JJB",
        residenceIndependent: true,

        schedules: [
          "Mardi 20h30-22h00",
          "Jeudi 20h00-21h30",
          "Samedi 12h30-14h00",
        ],

        prices: {
          lhay: 350,
          antony: 350,
          outside: 350,
        },

        mainTariffLabel: "Formule JJB seul",

        complementaryTariff: {
          label: "Formule JJB + Judo",
          value: 450,
          prefix: "À partir de",
        },

        note: "La formule JJB permet également d'accéder au cours du mercredi au Dojo David Douillet.",
      },
      {
        code: "JJB - ENFANTS - VR",
        name: "Jiu-Jitsu Brésilien - Enfants",
        birth: "7 à 12 ans",
        discipline: "JJB",
        residenceIndependent: true,

        schedules: ["Samedi 11h45-12h30"],

        prices: {
          lhay: 250,
          antony: 250,
          outside: 250,
        },

        mainTariffLabel: "Formule JJB seul",

        complementaryTariff: {
          label: "Formule JJB + Judo",
          value: 360,
          prefix: "À partir de",
        },

        note: "La formule JJB permet également d'accéder au cours du mercredi au Dojo David Douillet.",
      },
    ],
  },
];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatEuro(value) {
  if (!Number.isFinite(value)) {
    return "Sur demande";
  }

  return `${value.toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
  })} €`;
}

function SelectField({ label, icon: Icon, value, onChange, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-primary" />

        {label}
      </span>

      <select
        value={value}
        onChange={onChange}
        className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 shadow-sm outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-orange-500/50 dark:focus:ring-orange-500/10"
      >
        {children}
      </select>
    </label>
  );
}

export default function TarifsResidenceCalculator({ className = "" }) {
  const firstDojo = DOJO_TARIFFS[0];
  const firstGroup = firstDojo?.groups?.[0];

  const [selectedDojoId, setSelectedDojoId] = useState(firstDojo?.id || "");

  const [selectedGroupCode, setSelectedGroupCode] = useState(
    firstGroup?.code || "",
  );

  const [selectedResidenceId, setSelectedResidenceId] = useState("lhay");

  // ---------------------------------------------------------------------------
  // DOJO SÉLECTIONNÉ
  // ---------------------------------------------------------------------------

  const selectedDojo = useMemo(() => {
    return DOJO_TARIFFS.find((dojo) => dojo.id === selectedDojoId);
  }, [selectedDojoId]);

  const groups = selectedDojo?.groups || [];

  // ---------------------------------------------------------------------------
  // GROUPE SÉLECTIONNÉ
  // ---------------------------------------------------------------------------

  const selectedGroup = useMemo(() => {
    return groups.find((group) => group.code === selectedGroupCode);
  }, [groups, selectedGroupCode]);

  // ---------------------------------------------------------------------------
  // RÉSIDENCE
  // ---------------------------------------------------------------------------

  const selectedResidence = useMemo(() => {
    return (
      RESIDENCE_OPTIONS.find(
        (residence) => residence.id === selectedResidenceId,
      ) || RESIDENCE_OPTIONS[0]
    );
  }, [selectedResidenceId]);

  // ---------------------------------------------------------------------------
  // TARIF
  // ---------------------------------------------------------------------------

  const selectedPrice = selectedGroup?.prices?.[selectedResidenceId];

  const hasPrice = Number.isFinite(selectedPrice);

  const isResidenceIndependent = selectedGroup?.residenceIndependent === true;

  const isJjb = selectedGroup?.discipline === "JJB";

  // ---------------------------------------------------------------------------
  // CHANGEMENT DE DOJO
  // ---------------------------------------------------------------------------

  const handleDojoChange = (event) => {
    const nextDojoId = event.target.value;

    const nextDojo = DOJO_TARIFFS.find((dojo) => dojo.id === nextDojoId);

    setSelectedDojoId(nextDojoId);

    setSelectedGroupCode(nextDojo?.groups?.[0]?.code || "");
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[2rem] border border-orange-200/70 bg-white shadow-xl shadow-orange-500/5 dark:border-orange-500/20 dark:bg-slate-900",
        className,
      )}
    >
      {/* -------------------------------------------------------------------- */}
      {/* FOND DÉCORATIF                                                      */}
      {/* -------------------------------------------------------------------- */}

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.16),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_30%)]" />

      <div className="relative grid gap-6 p-5 sm:p-6 lg:grid-cols-[1.1fr_0.9fr] lg:p-8">
        {/* ------------------------------------------------------------------ */}
        {/* COLONNE GAUCHE                                                    */}
        {/* ------------------------------------------------------------------ */}

        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
            <FaCalculator />
            Simulateur de tarif
          </div>

          <h3 className="mt-5 font-barlow text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
            Calculez automatiquement le tarif annuel
          </h3>

          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
            Sélectionnez le dojo et le groupe souhaité. Pour les cours de Judo,
            le tarif dépend également du lieu de résidence. Les tarifs JJB sont
            identiques quelle que soit la commune.
          </p>

          <div className="mt-6 grid gap-4">
            {/* ---------------------------------------------------------------- */}
            {/* DOJO                                                             */}
            {/* ---------------------------------------------------------------- */}

            <SelectField
              label="Choisir le dojo"
              icon={FaMapMarkerAlt}
              value={selectedDojoId}
              onChange={handleDojoChange}
            >
              {DOJO_TARIFFS.map((dojo) => (
                <option key={dojo.id} value={dojo.id}>
                  {dojo.name}
                </option>
              ))}
            </SelectField>

            {/* ---------------------------------------------------------------- */}
            {/* GROUPE                                                           */}
            {/* ---------------------------------------------------------------- */}

            <SelectField
              label="Choisir le groupe"
              icon={FaLayerGroup}
              value={selectedGroupCode}
              onChange={(event) => setSelectedGroupCode(event.target.value)}
            >
              {groups.map((group) => (
                <option key={group.code} value={group.code}>
                  {group.code} — {group.name}
                  {group.birth ? ` (${group.birth})` : ""}
                </option>
              ))}
            </SelectField>

            {/* ---------------------------------------------------------------- */}
            {/* RÉSIDENCE                                                        */}
            {/* ---------------------------------------------------------------- */}

            {!isResidenceIndependent ? (
              <div>
                <p className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  <FaHome className="text-primary" />
                  Lieu de résidence
                </p>

                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {RESIDENCE_OPTIONS.map((residence) => {
                    const isSelected = selectedResidenceId === residence.id;

                    return (
                      <button
                        key={residence.id}
                        type="button"
                        onClick={() => setSelectedResidenceId(residence.id)}
                        className={cn(
                          "rounded-2xl border px-4 py-3 text-left transition",
                          isSelected
                            ? "border-orange-300 bg-orange-50 text-orange-900 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-100"
                            : "border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:bg-orange-50/70 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-white/5",
                        )}
                      >
                        <span className="flex items-center gap-2 text-sm font-bold">
                          {isSelected && <FaCheckCircle />}

                          {residence.shortLabel}
                        </span>

                        <span className="mt-1 block text-xs opacity-70">
                          {residence.helper}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                    <FaCheckCircle />
                  </div>

                  <div>
                    <p className="text-sm font-bold text-emerald-900 dark:text-emerald-100">
                      Tarif JJB unique
                    </p>

                    <p className="mt-1 text-sm leading-6 text-emerald-700 dark:text-emerald-300">
                      Le tarif du Jiu-Jitsu Brésilien est identique quelle que
                      soit votre commune de résidence.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* COLONNE DROITE                                                    */}
        {/* ------------------------------------------------------------------ */}

        <aside className="flex flex-col justify-between rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-950/70">
          <div>
            {/* -------------------------------------------------------------- */}
            {/* TARIF PRINCIPAL                                               */}
            {/* -------------------------------------------------------------- */}

            <div className="rounded-[1.5rem] bg-slate-950 p-5 text-white shadow-xl dark:bg-white dark:text-slate-950">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] opacity-60">
                    {selectedGroup?.mainTariffLabel || "Tarif annuel"}
                  </p>

                  <p className="mt-2 font-barlow text-5xl font-bold">
                    {formatEuro(selectedPrice)}
                  </p>
                </div>

                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-2xl dark:bg-slate-950/10">
                  <FaEuroSign />
                </div>
              </div>

              <p className="mt-4 text-sm font-semibold opacity-80">
                {isResidenceIndependent
                  ? `${selectedGroup?.name || ""} · Tarif annuel`
                  : `${selectedResidence?.shortLabel} · ${
                      selectedGroup?.code || ""
                    }`}
              </p>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* TARIF JJB + JUDO                                              */}
            {/* -------------------------------------------------------------- */}

            {selectedGroup?.complementaryTariff && (
              <div className="mt-4 rounded-[1.5rem] border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-5 dark:border-orange-500/20 dark:from-orange-500/10 dark:to-amber-500/5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-600 dark:text-orange-300">
                      {selectedGroup.complementaryTariff.label}
                    </p>

                    <p className="mt-2 font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                      {selectedGroup.complementaryTariff.prefix &&
                        `${selectedGroup.complementaryTariff.prefix} `}

                      {formatEuro(selectedGroup.complementaryTariff.value)}
                    </p>
                  </div>

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300">
                    <FaEuroSign />
                  </div>
                </div>

                <p className="mt-3 text-xs leading-5 text-slate-600 dark:text-slate-300">
                  Cette formule permet de pratiquer le Jiu-Jitsu Brésilien et le
                  Judo.
                </p>
              </div>
            )}

            {/* -------------------------------------------------------------- */}
            {/* INFORMATIONS DU GROUPE                                        */}
            {/* -------------------------------------------------------------- */}

            <div className="mt-5 space-y-3">
              {/* ------------------------------------------------------------ */}
              {/* GROUPE                                                       */}
              {/* ------------------------------------------------------------ */}

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Groupe sélectionné
                </p>

                <p className="mt-1 font-barlow text-xl font-bold text-slate-950 dark:text-white">
                  {selectedGroup?.name}
                </p>

                {selectedGroup?.birth && (
                  <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {selectedGroup.birth}
                  </p>
                )}

                {selectedGroup?.discipline && (
                  <span className="mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-600 dark:bg-white/10 dark:text-slate-300">
                    {selectedGroup.discipline}
                  </span>
                )}

                {selectedGroup?.note && (
                  <p className="mt-3 rounded-xl bg-orange-50 px-3 py-2 text-xs font-semibold leading-5 text-orange-800 dark:bg-orange-500/10 dark:text-orange-200">
                    {selectedGroup.note}
                  </p>
                )}
              </div>

              {/* ------------------------------------------------------------ */}
              {/* HORAIRES                                                     */}
              {/* ------------------------------------------------------------ */}

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
                <p className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white">
                  <FaCalendarAlt className="text-primary" />
                  Horaires
                </p>

                <div className="space-y-2">
                  {(selectedGroup?.schedules || []).map((schedule, index) => (
                    <p
                      key={`${schedule}-${index}`}
                      className="rounded-xl bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-600 dark:bg-slate-950 dark:text-slate-300"
                    >
                      {schedule}
                    </p>
                  ))}
                </div>
              </div>

              {/* ------------------------------------------------------------ */}
              {/* DOJO                                                         */}
              {/* ------------------------------------------------------------ */}

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
                <p className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-950 dark:text-white">
                  <FaMapMarkerAlt className="text-primary" />
                  Dojo
                </p>

                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  {selectedDojo?.name}
                </p>

                <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  {selectedDojo?.address}
                </p>
              </div>
            </div>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* NOTE DE BAS DE CARTE                                            */}
          {/* ---------------------------------------------------------------- */}

          <p className="mt-5 flex items-start gap-2 rounded-2xl bg-white p-3 text-xs leading-5 text-slate-500 dark:bg-white/5 dark:text-slate-400">
            <FaInfoCircle className="mt-0.5 shrink-0 text-primary" />

            {hasPrice ? (
              isJjb ? (
                <>
                  Le montant affiché correspond à la formule JJB seul. Une
                  formule combinée JJB + Judo est également proposée par le
                  club.
                </>
              ) : (
                <>
                  Le montant affiché correspond à la grille tarifaire 2026 /
                  2027 fournie par le club.
                </>
              )
            ) : (
              <>
                Ce cours n’a pas de tarif indiqué dans la grille. Le montant est
                à confirmer auprès du club.
              </>
            )}
          </p>
        </aside>
      </div>
    </div>
  );
}
